<script setup>
import { computed, ref } from 'vue'
import { ChevronRight } from '@lucide/vue'
import { Badge } from '@drama/ui'

import AuditDiagnostics from './AuditDiagnostics.vue'
import AuditId from './AuditId.vue'
import { clip, clockTime, duration, elapsedMs, fullTime, useAuditText, variantOf } from './audit-text.js'
import { asArray } from './run-observer.js'

const props = defineProps({
  snapshot: { type: Object, required: true },
  // callOwners() 的结果。
  owners: { type: Object, required: true },
})

const { tr, enumLabel, errorLabel } = useAuditText()
// 全部回合请求（跨分支），含没有落成回合的失败与取消请求，按开始时间升序。
const rows = computed(() => Object.values(props.snapshot.requests ?? {})
  .sort((a, b) => String(a.started_at ?? '').localeCompare(String(b.started_at ?? '')))
  .map((request) => ({ request, diagnostics: asArray(request.diagnostics) })))
// 展开了校验反馈的请求；反馈按展开懒渲染，模型原文可能很长。
const expanded = ref(new Set())

function toggle(requestId) {
  if (expanded.value.has(requestId)) expanded.value.delete(requestId)
  else expanded.value.add(requestId)
}

function inputLabel(request) {
  const submitted = request.submitted_input
  if (!submitted) return '—'
  const mode = enumLabel('turns.modes', submitted.mode)
  return submitted.text ? `${mode} · ${submitted.text}` : mode
}

function turnLabel(requestId) {
  return props.owners.turn_of_request.has(requestId) ? tr('turns.turn', { seq: props.owners.turn_of_request.get(requestId) }) : '—'
}
</script>

<template>
  <div v-if="rows.length" class="au-table-wrap">
    <table class="au-table" data-testid="observer-requests">
      <thead>
        <tr>
          <th>{{ tr('requests.time') }}</th>
          <th>{{ tr('requests.turn') }}</th>
          <th>{{ tr('requests.input') }}</th>
          <th>{{ tr('requests.status') }}</th>
          <th>{{ tr('requests.error') }}</th>
          <th class="is-num">{{ tr('requests.elapsed') }}</th>
          <th class="is-num">{{ tr('requests.points') }}</th>
          <th>{{ tr('requests.id') }}</th>
          <th>{{ tr('requests.diagnostics') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="{ request, diagnostics } in rows" :key="request.request_id">
          <tr :class="{ 'is-open': expanded.has(request.request_id) }">
            <td :title="fullTime(request.started_at)">{{ clockTime(request.started_at) }}</td>
            <td>{{ turnLabel(request.request_id) }}</td>
            <td :title="inputLabel(request)">{{ clip(inputLabel(request), 120) }}</td>
            <td><Badge size="sm" :variant="variantOf(request.status)">{{ enumLabel('requestStatuses', request.status) }}</Badge></td>
            <td>
              <span v-if="request.error_code" class="au-error" :title="request.error_code">{{ errorLabel(request.error_code) }}</span>
              <span v-else class="au-muted">—</span>
            </td>
            <td class="is-num">{{ duration(elapsedMs(request.started_at, request.completed_at)) }}</td>
            <td class="is-num">{{ request.charged_points ?? '—' }}</td>
            <td><AuditId :id="request.request_id" /></td>
            <td>
              <button
                v-if="diagnostics.length"
                type="button"
                class="au-disclosure"
                :aria-expanded="expanded.has(request.request_id) ? 'true' : 'false'"
                :aria-label="tr('requests.diagnosticsExpand', { count: diagnostics.length })"
                :data-testid="`observer-request-diagnostics-toggle-${request.request_id}`"
                @click="toggle(request.request_id)"
              >
                <ChevronRight :size="14" aria-hidden="true" />{{ tr('requests.diagnosticsCount', { count: diagnostics.length }) }}
              </button>
              <span v-else class="au-muted">—</span>
            </td>
          </tr>
          <tr v-if="expanded.has(request.request_id)" class="au-table__detail">
            <td colspan="9"><AuditDiagnostics :diagnostics="diagnostics" /></td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  <p v-else class="au-empty">{{ tr('empty') }}</p>
</template>
